import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import currency from 'currency.js';
import { sortByProperties } from 'donor-elf-web/lib/array-utils';
import ContactModel from 'donor-elf-web/models/contact';
import type { ContactLastGiftToOtherFund } from 'donor-elf-web/models/contact-detail';
import LineItemModel from 'donor-elf-web/models/line-item';
import FormatAmountService from 'donor-elf-web/services/format-amount-service';
import FundGoalService from 'donor-elf-web/services/fund-goal-service';
import FundsService from 'donor-elf-web/services/funds-service';
import Highcharts from 'highcharts';
import moment from 'moment';

interface ComponentArgs {
  contact: ContactModel;
  model: LineItemModel[];
  lastGiftToOtherFunds: ContactLastGiftToOtherFund[];
}

export default class ContactDetailsGiftComponent extends Component<ComponentArgs> {
  @service declare formatAmountService: FormatAmountService;
  @service declare fundsService: FundsService;
  @service declare fundGoalService: FundGoalService;

  giftHistoryNumberMonths = 36;

  get gifts() {
    return this.args.model.filter((x) => x.amount > 0);
  }

  get hasGifts() {
    return this.gifts.length > 0;
  }

  get firstGiftDate() {
    return this.gifts.at(-1)?.transDate;
  }

  get lastGiftDate() {
    return this.gifts[0]?.transDate;
  }

  get numberGifts() {
    return this.gifts.length;
  }

  get totalGiftAmount() {
    return this.gifts
      .map((x) => x.amount)
      .reduce((prevVal, amount) => {
        return currency(prevVal).add(amount).value;
      }, 0);
  }

  get averageGiftAmount() {
    return this.totalGiftAmount / this.numberGifts;
  }

  get hasYtdGoalType() {
    return this.fundGoalService.hasYtdGoalType;
  }

  get ytdGiftAmount() {
    let ytdStartDate = this.fundGoalService.ytdStartMoment!.toDate();
    return this.gifts
      .filter((x) => x.transDate >= ytdStartDate)
      .reduce((prevVal, item) => {
        return currency(prevVal).add(item.amount).value;
      }, 0);
  }

  get lastGiftToOtherFundItems() {
    return sortByProperties(
      this.args.lastGiftToOtherFunds.map((x) => {
        return {
          ...x,
          contactDetailUrl: this.fundsService.viewFundUrl(x.fund_id, {
            urlPath: `contacts/${x.contact_id}`,
          }),
        };
      }),
      ['trans_date:desc'],
    );
  }

  get giftHistorySeriesData() {
    let gifts = this.gifts;
    let numberMonths = this.giftHistoryNumberMonths;
    let giftAmountByMonth: any = {};
    let today = new Date();
    let currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    currentMonth.setMonth(currentMonth.getMonth() + 1);
    for (let i = 0; i < numberMonths; i++) {
      currentMonth.setMonth(currentMonth.getMonth() - 1);
      let month = `${currentMonth.getFullYear()}/${currentMonth.getMonth() + 1}`;
      giftAmountByMonth[month] = 0;
    }
    gifts.forEach(function (gift) {
      let giftDate = gift.transDate;
      let month = `${giftDate.getFullYear()}/${giftDate.getMonth() + 1}`;
      if (giftAmountByMonth[month] != null) {
        giftAmountByMonth[month] = currency(giftAmountByMonth[month]).add(
          gift.amount,
        ).value;
      }
    });
    let giftAmountsAndMonth = [];
    currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    currentMonth.setMonth(currentMonth.getMonth() - 36);
    for (let i = 0; i < numberMonths; i++) {
      currentMonth.setMonth(currentMonth.getMonth() + 1);
      let month = `${currentMonth.getFullYear()}/${currentMonth.getMonth() + 1}`;
      giftAmountsAndMonth.push(giftAmountByMonth[month]);
    }
    // Have to replace 0's with nulls so the 'minPointLength' plot option
    // doesn't show a bar for a 0 amount
    return giftAmountsAndMonth.map((x) => (x == 0 ? null : x));
  }

  get giftHistoryCategories() {
    let categories = [];
    let numberMonths = this.giftHistoryNumberMonths;
    let today = new Date();
    let currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    currentMonth.setMonth(currentMonth.getMonth() - 36);
    for (let i = 0; i < numberMonths; i++) {
      currentMonth.setMonth(currentMonth.getMonth() + 1);
      categories.push(moment(currentMonth).format('MMMM YYYY'));
    }
    return categories;
  }

  get chartSettings() {
    let self = this;
    return {
      chart: {
        type: 'column',
        margin: [0, 0, 15, 0],
      },
      title: undefined,
      credits: {
        enabled: false,
      },
      xAxis: {
        categories: this.giftHistoryCategories,
        tickInterval: 12,
        tickLength: 0,
        labels: {
          autoRotation: false,
          align: 'left',
          y: 13,
        },
      },
      yAxis: {
        min: 0,
        maxPadding: 0.0,
        visible: false,
        endOnTick: false,
      },
      legend: {
        enabled: false,
      },
      tooltip: {
        formatter: function () {
          return `${this.category}: <b>${self.formatAmountService.format(this.y)}</b>`;
        },
      },
      plotOptions: {
        column: {
          borderWidth: 1.0,
          pointPadding: 0.0,
          groupPadding: 0.0,
          color: '#0b68a1',
          minPointLength: 3,
        },
      },
      series: [
        {
          data: this.giftHistorySeriesData,
        },
      ],
    } as unknown as Highcharts.Options;
  }

  @action
  chartElementDidInsert(el: any) {
    Highcharts.chart(el, this.chartSettings);
  }
}
